/**
 * Lightweight, dependency-free JWT payload reader.
 *
 * IMPORTANT: this does NOT verify the token's signature — the backend is the
 * only party that can do that (see backend/src/middleware/authMiddleware.js).
 * This is used purely so the SPA can pre-emptively treat an expired/malformed
 * token as "logged out" instead of rendering protected UI with stale
 * credentials and waiting for an API call to fail. All real authorization
 * still happens server-side on every request.
 */

function base64UrlDecode(input) {
  const padded = input
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .padEnd(input.length + ((4 - (input.length % 4)) % 4), "=");
  return atob(padded);
}

export function decodeJwt(token) {
  try {
    const [, payload] = token.split(".");
    if (!payload) return null;
    return JSON.parse(base64UrlDecode(payload));
  } catch {
    return null;
  }
}

/** True if the token is missing, malformed, or past its exp claim. */
export function isTokenExpired(token) {
  if (!token) return true;
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;
  // exp is in seconds; small skew buffer so we don't race a token that's
  // about to expire mid-request.
  return payload.exp * 1000 <= Date.now() + 5000;
}

/** Role scope embedded in the signed token itself (not the cached user object). */
export function tokenRoleScope(token) {
  if (!token) return null;
  return decodeJwt(token)?.roleScope ?? null;
}
